import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { getLocaleText } from "shared/boot/i18n";
import { formatIntlDate } from "shared/helpers/date";
import { fractionToPercentage, isNumber } from "shared/helpers/number";
import OrganisationsService, {
  type OrganisationUsage,
} from "shared/services/api/OrganisationsService";
import useUserStore from "shared/stores/user";
import { type Nullable, type Nullish } from "shared/types";

import { buildDefaultOrganisationUsage } from "../helpers/organisationUsage";

interface AIVisibilityProjectStats {
  count: Nullish<number>;
  max: Nullable<number>;
}

interface AIVisibilityUsage {
  fraction: number;
  hasReachedLimit: boolean;
  isActive: boolean;
  percentageFormatted: string;
  progress: number;
  refreshDate: string;
  remaining: number;
  resetDate: string;
  resetDateFormatted: string;
  total: number;
}

export const useOrganisationUsageStore = defineStore(
  "organisationUsage",
  () => {
    const userStore = useUserStore();
    const { organisation } = storeToRefs(userStore);

    const isLoadingOrganisationUsage = ref<boolean>(false);
    const hasOrganisationUsageLoadError = ref<boolean>(false);

    const organisationUsage = ref<OrganisationUsage>(
      buildDefaultOrganisationUsage()
    );

    const aiVisibilityProjectStats = computed<AIVisibilityProjectStats>(() => {
      const {
        instant_insights_reports_with_monitoring_count: count,
        max_instant_insights_reports_with_monitoring: max,
      } = organisationUsage.value;

      return {
        count,
        max,
      };
    });

    const aiVisibilityUsage = computed<AIVisibilityUsage>(() => {
      const total = organisationUsage.value.ai_visibility_credits_total ?? 0;

      const progress =
        organisationUsage.value.ai_visibility_credits_consumed ?? 0;

      const remaining = total ? Math.max(total - progress, 0) : 0;

      const fraction = total ? progress / total : 0;

      const percentageFormatted = total
        ? fractionToPercentage(fraction, 0)
        : "";

      const isActive = Boolean(
        organisationUsage.value.ai_visibility_credits_subscription_active
      );

      const refreshDate =
        organisationUsage.value.ai_visibility_credits_synced_at ?? "";

      const resetDate =
        organisationUsage.value.ai_visibility_credits_period_end ?? "";

      const resetDateFormatted = resetDate
        ? formatIntlDate(resetDate, {
            dateStyle: "medium",
          })
        : "";

      const hasReachedLimit =
        remaining <= 0 &&
        organisationUsage.value.ai_visibility_credits_total !== null;

      return {
        fraction,
        hasReachedLimit,
        isActive,
        percentageFormatted,
        progress,
        refreshDate,
        remaining,
        resetDate,
        resetDateFormatted,
        total,
      };
    });

    const hasReachedAIVisibilityProjectLimit = computed<boolean>(() => {
      const { count, max } = aiVisibilityProjectStats.value;

      if (isNumber(count) && isNumber(max)) {
        return count >= max;
      }

      return false;
    });

    async function loadOrganisationUsage() {
      if (isLoadingOrganisationUsage.value) return;
      isLoadingOrganisationUsage.value = true;
      hasOrganisationUsageLoadError.value = false;

      try {
        const { data } = await OrganisationsService.usage(
          organisation.value.id
        );

        organisationUsage.value = data;
      } catch (error) {
        hasOrganisationUsageLoadError.value = true;
        ErrorDialog(getLocaleText("global.errors.error_loading_data"), error);
      } finally {
        isLoadingOrganisationUsage.value = false;
      }
    }

    return {
      aiVisibilityProjectStats,
      aiVisibilityUsage,
      hasOrganisationUsageLoadError,
      hasReachedAIVisibilityProjectLimit,
      isLoadingOrganisationUsage,
      loadOrganisationUsage,
      organisationUsage,
    };
  }
);
